// /api-dokumentasjon — hvordan leverandører bruker BMAx-API-et.
//
// Synlig for admin og leverandør. Innholdet speiler det API-nøklene fra
// Innstillinger → API-nøkler faktisk gir tilgang til: leverandørrollens
// actions mot /connect, autentisert med headeren X-Bmax-Api-Key. Utvides
// VENDOR_ACTIONS i backend/src/routes/connect.js, skal tabellen her utvides
// tilsvarende.

import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import {
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";

const BASE_URL = "https://bmax.no/connect";

const CODE_SX = {
  display: "block",
  whiteSpace: "pre",
  overflowX: "auto",
  background: "#1f2937",
  color: "#e5e7eb",
  borderRadius: 2,
  p: 2,
  fontSize: 13,
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
};

const Code = ({ children }) => <SoftBox component="pre" sx={CODE_SX}>{children}</SoftBox>;
Code.propTypes = { children: PropTypes.node.isRequired };

const Section = ({ title, children }) => (
  <SoftBox mb={4}>
    <SoftTypography variant="h6" fontWeight="bold" mb={1}>
      {title}
    </SoftTypography>
    {children}
  </SoftBox>
);
Section.propTypes = { title: PropTypes.string.isRequired, children: PropTypes.node.isRequired };

const ACTIONS = [
  {
    action: "vendor_orders",
    desc: "Liste over ordrer tildelt leverandøren, med status og totaler. Paginert.",
    payload: `{ "action": "vendor_orders", "limit": 10, "page_number": 1 }`,
  },
  {
    action: "order_detail",
    desc: "Full detalj for én ordre. Svarer bare for ordrer som tilhører leverandøren — andre ordre-id-er gir order_not_found.",
    payload: `{ "action": "order_detail", "order_id": 123 }`,
  },
  {
    action: "change_request_by_vendor",
    desc: "Be om endring av leveringsdato/-tid på en tildelt ordre (rcd = dato YYYY-MM-DD, rct = klokkeslett HH:mm, t = valgfritt notat). Admin godkjenner eller avslår.",
    payload: `{ "action": "change_request_by_vendor", "order_id": 123, "rcd": "2026-11-01", "rct": "08:00", "t": "Bil på verksted" }`,
  },
  {
    action: "cancel_request_by_vendor",
    desc: "Trekk en tidligere innsendt endringsforespørsel.",
    payload: `{ "action": "cancel_request_by_vendor", "order_id": 123 }`,
  },
];

function ApiDocs() {
  return (
    <DashboardLayout>
      <SoftBox py={3}>
        <Card sx={{ p: { xs: 2, md: 4 } }}>
          <SoftTypography variant="h4" fontWeight="bold">
            BMAx API for leverandører
          </SoftTypography>
          <SoftTypography variant="body2" color="text" mt={1} mb={3}>
            Dette API-et lar leverandørens egne systemer hente og følge opp ordrer hos BMAx
            maskinelt. Tilgangen styres av en API-nøkkel som Betongsentrum utsteder per
            leverandør (admin: Innstillinger → API-nøkler).
          </SoftTypography>
          <Divider sx={{ mb: 3 }} />

          <Section title="Grunnprinsipper">
            <SoftTypography variant="body2" component="div" color="text">
              <ul style={{ paddingLeft: 20, margin: 0 }}>
                <li>Alle kall er <b>HTTP POST</b> mot ett endepunkt: <code>{BASE_URL}</code></li>
                <li>Kroppen er JSON med feltet <code>action</code> som velger operasjon.</li>
                <li>Svar er alltid JSON. Transportfeil bruker HTTP-statuskoder (401/403), forretningsfeil svarer 200 med <code>{`{ "status": false, "message": … }`}</code>.</li>
                <li>Rate-limit: <b>120 kall per minutt</b> per IP. Over grensen svarer API-et 429 — vent og prøv igjen.</li>
              </ul>
            </SoftTypography>
          </Section>

          <Section title="Autentisering">
            <SoftTypography variant="body2" color="text" mb={1}>
              Send API-nøkkelen i headeren <code>X-Bmax-Api-Key</code> på hvert kall. Nøkkelen
              identifiserer leverandøren og gir kun tilgang til leverandørens egne ordrer —
              aldri andres.
            </SoftTypography>
            <Code>{`POST ${BASE_URL}
Content-Type: application/json
X-Bmax-Api-Key: bmx_DIN_NØKKEL_HER`}</Code>
            <SoftTypography variant="body2" color="text" mt={1}>
              Manglende eller ugyldig nøkkel gir{" "}
              <code>{'401 { "ok": false, "error": "unauthorized" }'}</code>. En tilbakekalt nøkkel
              slutter å virke umiddelbart.
            </SoftTypography>
            <SoftBox mt={1.5}>
              <Chip
                size="small"
                color="warning"
                label="Nøkkelen er et passord: aldri i URL-er, klientkode eller versjonskontroll."
              />
            </SoftBox>
          </Section>

          <Section title="Tilgjengelige operasjoner">
            <TableContainer sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead sx={{ display: "table-header-group" }}>
                  <TableRow>
                    <TableCell>Action</TableCell>
                    <TableCell>Beskrivelse</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ACTIONS.map((a) => (
                    <TableRow key={a.action}>
                      <TableCell>
                        <code>{a.action}</code>
                      </TableCell>
                      <TableCell>
                        <SoftTypography variant="body2" color="text">
                          {a.desc}
                        </SoftTypography>
                        <SoftBox component="code" sx={{ fontSize: 12, color: "#64748b" }}>
                          {a.payload}
                        </SoftBox>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Section>

          <Section title="Eksempel: hent ordrelisten">
            <Code>{`curl -X POST ${BASE_URL} \\
  -H "Content-Type: application/json" \\
  -H "X-Bmax-Api-Key: bmx_DIN_NØKKEL_HER" \\
  -d '{ "action": "vendor_orders", "limit": 10, "page_number": 1 }'`}</Code>
            <SoftTypography variant="body2" color="text" mt={1} mb={1}>
              Svar (forkortet):
            </SoftTypography>
            <Code>{`{
  "status": true,
  "data_info": { "total_records": 42, "showing": 10, "total_pages": 5 },
  "data": [
    {
      "order_id": 123,
      "status": "Venter på produsent",
      "date": "01.11.2026",
      "time": "08:00",
      "total": "12 500,00,-",
      "number_of_items": 2
    }
  ]
}`}</Code>
          </Section>

          <Section title="Eksempel: hent én ordre">
            <Code>{`curl -X POST ${BASE_URL} \\
  -H "Content-Type: application/json" \\
  -H "X-Bmax-Api-Key: bmx_DIN_NØKKEL_HER" \\
  -d '{ "action": "order_detail", "order_id": 123 }'`}</Code>
            <SoftTypography variant="body2" color="text" mt={1}>
              Svaret inneholder leveringsadresse, leveranser (volum, biltype, betongkvalitet),
              status og beløp. En ordre som ikke tilhører leverandøren svarer{" "}
              <code>{`{ "status": false, "message": "order_not_found" }`}</code> — API-et bekrefter
              aldri om andres ordre-id-er finnes.
            </SoftTypography>
          </Section>

          <Section title="Feilhåndtering">
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ display: "table-header-group" }}>
                  <TableRow>
                    <TableCell>Svar</TableCell>
                    <TableCell>Betydning</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell><code>401 unauthorized</code></TableCell>
                    <TableCell>Manglende, ugyldig eller tilbakekalt nøkkel.</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><code>403 forbidden</code></TableCell>
                    <TableCell>Operasjonen er utenfor leverandørrollen.</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><code>429</code></TableCell>
                    <TableCell>Rate-limit (120 kall/minutt). Vent og prøv igjen.</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><code>{`200 { "status": false }`}</code></TableCell>
                    <TableCell>
                      Forretningsfeil — detaljer i <code>message</code> (f.eks.{" "}
                      <code>order_not_found</code>).
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Section>

          <Divider sx={{ mb: 2 }} />
          <SoftTypography variant="caption" color="text">
            Spørsmål eller behov for flere operasjoner? Kontakt Betongsentrum — nye operasjoner
            legges til leverandørrollen sentralt og dokumenteres her.
          </SoftTypography>
        </Card>
      </SoftBox>
    </DashboardLayout>
  );
}

export default ApiDocs;
